/**
 * Principal Login - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft } from 'lucide-react';
import Navigation from '../components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        identifier: email.trim(),
        password,
      });
      if (data.role !== 'principal') {
        setError(`This account is a ${data.role}. Please use the correct login page or try /login for the unified sign-in.`);
        setLoading(false);
        return;
      }
      Object.entries(data.storage || {}).forEach(([k, v]) => localStorage.setItem(k, v));
      // Backward compatibility — older pages read principal_user from localStorage
      localStorage.setItem('principal_user', JSON.stringify(data.user));
      navigate('/principal/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-body">
      <Navigation />

      <Link
        to="/"
        className="fixed top-20 left-4 md:top-24 md:left-6 inline-flex items-center gap-1 text-slate-700 hover:text-sanjaya-navy hover:underline font-medium min-h-[44px] z-40"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Home
      </Link>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="shadow-xl border-2 border-slate-200">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                {/* Brand Logo */}
                <div className="w-20 h-20 bg-sanjaya-navy rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-sanjaya-orange font-heading font-bold text-3xl">स</span>
                </div>
                <h1 className="text-3xl font-bold text-sanjaya-navy font-heading mb-2">Principal Login</h1>
                <p className="text-slate-600">School Administration Portal</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-heading">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="principal@school.edu"
                    required
                    className="h-12 text-base border-2 border-slate-200 focus:border-sanjaya-navy"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 font-heading">
                    Password
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="h-12 text-base border-2 border-slate-200 focus:border-sanjaya-navy"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 p-4 rounded-xl text-sm border border-red-200">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-base font-bold bg-sanjaya-navy hover:bg-sanjaya-navy-dark text-white"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrincipalLogin;
